import type { Effect, Store } from 'effector'
import type { Location, RouterResponse, Staged } from '~/core/router'

import { preacquireWakeLock } from '@setplex/pureya'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  launch,
  sample,
  scopeBind,
} from 'effector'
import { features, options } from '~/core/flags'
import { register } from '~/core/repository'
import {
  Action,
  bannerSlide,
  hooks,
  model as router,
  scroll,
  stage,
  router as universalRouter,
  getErrorMessage,
} from '~/core/router'
import { model as session } from '~/entities/session'
import { exists, nay, noop } from '~/shared/helpers'
import { Logger } from '~/shared/logger'
import { accessible, warden } from './jail'

/** Keep Screen Wake Lock request in the same sync turn as user-gesture navigations to /player/* */
const preacquirePlayerWakeLockFx = createEffect(preacquireWakeLock)

sample({
  clock: router.navigatePush,
  filter: (to) => {
    const path = typeof to === 'string' ? to : (to.pathname ?? '')
    return path.startsWith('/player/')
  },
  target: preacquirePlayerWakeLockFx,
})

export const logger = new Logger('tria/processes/navigation')

// initialize process
export const init = createEvent()

// start preparations to render exact route (e.g. call `wait` effect)
const prerender = createEvent<RouterResponse>()

// start actual render of exact route
const render = createEvent<RouterResponse>()

// pre-set route-to-be
const setRoute = createEvent<RouterResponse>()

// try route agains current jail cage
const trialRoute = createEvent<RouterResponse>()

// current loaded route
export const $route = createStore<RouterResponse | null>(null)

// resolve route from location, using universal router
const resolveRouteFx: Effect<Location, RouterResponse | null | undefined> =
  attach({
    source: {
      options: options.$optionsFlags,
      features: features.$featuredFlags,
      session: session.$session, // TODO: session is not available on page reload :(
    },
    async effect({ options, features, session }, location: Location) {
      // it is safe to use `store.getState()` here, because it is called inside `scopeBind`
      // eslint-disable-next-line effector/no-getState
      const read = scopeBind((source: Store<unknown>) => source.getState(), { safe: true }) // prettier-ignore
      return universalRouter.resolve({
        pathname: location.pathname,
        query: new URLSearchParams(location.search),
        options,
        features,
        session,
        read,
      })
    },
  })

// init non-blocking calls in parallel with render
const initFx: Effect<RouterResponse & Staged, void> = createEffect<
  RouterResponse & Staged,
  void
>((route) => {
  hooks.trigger(hooks.Init, { route })
  if (route.init) {
    // `init` could be any effector's unit, usually an Event
    launch({
      target: route.init,
      params: { ...route.props, stage: route.stage },
    })
  }
})

// trigger left route actions
const leftFx: Effect<RouterResponse, void> = attach({
  source: $route,
  effect(fromRoute, toRoute: RouterResponse) {
    if (fromRoute && toRoute && fromRoute.__resolved !== toRoute.__resolved) {
      hooks.trigger(hooks.Left, { route: fromRoute, to: toRoute })
      if (fromRoute.left) {
        // `left` could be any effector's unit, usually an Event
        // can pass from and to routes here, if needed
        launch({
          target: fromRoute.left,
          params: { ...fromRoute.props },
        })
      }
    }
  },
})

// effect to render application, should be implemented _outside_ of model
export const renderFx: Effect<
  { location: Location; route: RouterResponse },
  void
> = createEffect()

//
// react on location change
//

// check /error state on init
sample({
  clock: init,
  source: router.$location,
  filter: (
    location: Location | null
  ): location is Location & { state: { params: Record<string, string> } } =>
    location != null &&
    location.state != null &&
    typeof location.state === 'object' &&
    'params' in location.state &&
    location.state.params != null &&
    typeof location.state.params === 'object' &&
    location.pathname.startsWith('/error'),
  fn: ({ state }) =>
    state.params.pathname + state.params.search + state.params.hash,
  target: router.navigateReplace,
})

// resolve route on each location change
sample({
  clock: router.$location,
  filter: exists,
  target: resolveRouteFx,
})

// when route is resolved - check, if we still on the same location, as we were before,
// and route exists at all
sample({
  clock: resolveRouteFx.done,
  source: router.$key,
  filter: (key, { params, result }) => params.key === key && exists(result),
  fn: (__, { result }) => result!,
  target: setRoute,
})

// some error happen when resolving route -> redirect to error page
sample({
  clock: resolveRouteFx.fail,
  filter: ({ params }) => nay(params.pathname.startsWith('/error')), // prevent infinite cycle
  fn: (state) => ({
    pathname: '/error',
    search: new URLSearchParams({
      from: `${state.params.pathname}${state.params.search}${state.params.hash}`,
    }).toString(),
    state: {
      ...state,
      errorMessage: getErrorMessage(state.error),
      error: getErrorMessage(state.error),
    },
  }),
  target: router.navigatePush,
})

// log resolve route error
sample({
  clock: resolveRouteFx.fail,
  fn: (fail) => ['🔁 router.resolve:', fail],
  target: logger.errorFx,
})

// start transaction on resolve route start
sample({
  clock: resolveRouteFx,
  fn: noop,
  target: router.transitionStart,
})

// finish transaction on resolve route finish
sample({
  clock: resolveRouteFx.finally,
  fn: noop,
  target: router.transitionEnd,
})

//
// check route validity
//

// handles redirects first
// in case new route has `redirect` field -> replace current location
sample({
  clock: setRoute,
  filter: (route) => exists(route.redirect),
  fn: (route) => route.redirect!,
  target: router.navigateReplace,
})

// in case new route has no `redirect` field,
// try route agains current jail cage
sample({
  clock: setRoute,
  filter: (route) => nay(exists(route.redirect)),
  target: trialRoute,
})

// if route cannot access current cage -> put it to trial
sample({
  clock: trialRoute,
  source: warden.$cage,
  filter: (cage, route) => {
    // console.log('🚀', cage, route, `accessible:${accessible(cage, route.keys)}`) // prettier-ignore
    return nay(accessible(cage, route.keys))
  },
  fn: (_, route) => route,
  target: warden.trial,
})

// if route can access current cage -> set it as new route (and clear trial)
sample({
  clock: trialRoute,
  source: warden.$cage,
  filter: (cage, route) => accessible(cage, route.keys),
  fn: (_, route) => route,
  target: [warden.clear, leftFx],
})

// and then -> actually set new route
sample({
  clock: leftFx.finally,
  fn: ({ params }) => params,
  target: [$route, warden.$route],
})

//
// update and render actual rendered route
//

// finally render requested and resolved route
sample({
  clock: $route,
  filter: exists,
  target: prerender,
})

// render and launch non-blocking initialization, if any
sample({
  clock: prerender,
  fn: getPrerenderStage,
  target: [initFx, render],
})

// call actual render effect with current location
sample({
  clock: render,
  source: router.$location,
  filter: (location, route) => exists(location) && exists(route),
  fn: (location, route) => ({ location: location!, route }),
  target: renderFx,
})

//
// Refresh current route
//

// in case refresh requested -> get last result of `prerender` event,
// which should be currently rendered route, and call `init` for that route
sample({
  clock: router.refresh,
  source: prerender,
  fn: stage.refresh,
  target: initFx,
})

//
// handle scroll position
//

// save scroll position of previous location
sample({
  clock: router.$previous,
  filter: (location): location is Location => exists(location?.key),
  fn: (location: Location) => location.key,
  target: scroll.pushFx,
})

// clear scroll position in case action is Push (= go to new page)
sample({
  clock: router.$action,
  source: router.$key,
  filter: (key, action): key is string => exists(key) && action === Action.Push,
  target: scroll.clearFx,
})

// clear saved banner carousel slides on forward navigation
sample({
  clock: router.$action,
  source: router.$key,
  filter: (key, action): key is string => exists(key) && action === Action.Push,
  target: bannerSlide.clearBannerSlidesFx,
})

// restore scroll position (or just scroll up)
sample({
  clock: renderFx.finally,
  source: router.$key,
  filter: exists,
  target: scroll.applyFx,
})

/** True from route resolve through leave + render (covers the gap before renderFx). */
export const $navigationRendering: Store<boolean> = combine(
  resolveRouteFx.pending,
  leftFx.pending,
  renderFx.pending,
  (resolving, leaving, rendering) => resolving || leaving || rendering
)

register($navigationRendering, '$processes/navigation/navigationRendering')

//
// Detect browser initial load / reload
//

let isInitialRouteRender = true

function getPrerenderStage(route: RouterResponse): RouterResponse & Staged {
  const isReloadStage = isInitialRouteRender && isBrowserReload()
  isInitialRouteRender = false
  return isReloadStage ? stage.reload(route) : stage.navigate(route)
}

function isBrowserReload(): boolean {
  const entry = performance.getEntriesByType('navigation')[0]

  if (
    typeof PerformanceNavigationTiming !== 'undefined' &&
    entry instanceof PerformanceNavigationTiming
  ) {
    return entry.type === 'reload'
  }

  return performance.navigation.type === performance.navigation.TYPE_RELOAD
}
